import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { DestinationPicker } from '@/components/destinations/DestinationPicker';
import {
  ManualDestinationForm,
  type ManualDestinationFormData,
} from '@/components/destinations/ManualDestinationForm';
import { DestinationSearchPanel } from '@/components/destinations/search/DestinationSearchPanel';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useProviderServiceDestinationsUpdateMutation } from '@/services/api/generated/providerDestinationsService';
import {
  type BaseTreatmentRecordRequestChannelSchema,
  type TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import type { DestinationOption } from '@/types/provider-destinations';
import {
  DestinationChannelType,
  DestinationScope,
  LEGACY_SCOPE_MAP,
  SaveMode,
  UI_MANUAL_EVIDENCE_TO_API_SOURCE,
} from '@/types/provider-destinations';

interface SelectDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: TreatmentRecordRequestSchema;
  providerId: string;
  matterId: string;
  provider?: MedicalProvider;
  onSuccess?: () => void;
}

type ViewMode = 'picker' | 'search' | 'manual';

const toRequestChannelType = (
  channelType: DestinationChannelType,
): 'fax' | 'email' | 'mail' | 'portal' => {
  switch (channelType) {
    case DestinationChannelType.FAX:
      return 'fax';
    case DestinationChannelType.EMAIL:
      return 'email';
    case DestinationChannelType.MAIL:
      return 'mail';
    case DestinationChannelType.PORTAL:
      return 'portal';
    default:
      return 'fax';
  }
};

const getScopeFromRequest = (
  request: TreatmentRecordRequestSchema,
): DestinationScope => {
  const firstProvidedRecord = request.provided_records?.[0];
  if (!firstProvidedRecord) return DestinationScope.RECORDS;
  return LEGACY_SCOPE_MAP.toScope(firstProvidedRecord) ?? DestinationScope.RECORDS;
};

const buildChannelPayloadFromDestination = (
  destination: DestinationOption,
  phoneNumber: string,
): BaseTreatmentRecordRequestChannelSchema => {
  const requestType = toRequestChannelType(destination.channel_type);
  const payload: BaseTreatmentRecordRequestChannelSchema = {
    type: requestType,
    phone_number: phoneNumber.trim() || null,
    provider_contact_unit_handle_id: null,
    provider_directory_contact_point_id: destination.contact_point_id,
  };

  if (requestType === 'fax') {
    payload.fax = destination.value;
  } else if (requestType === 'email') {
    payload.email = destination.value;
  } else if (requestType === 'mail') {
    payload.address = destination.value;
  } else if (requestType === 'portal') {
    payload.instructions = destination.value;
  }

  return payload;
};

const buildChannelPayloadFromManual = (
  data: ManualDestinationFormData,
  phoneNumber: string,
  contactPointId?: string | null,
): BaseTreatmentRecordRequestChannelSchema => {
  const requestType = toRequestChannelType(data.channel_type);
  const payload: BaseTreatmentRecordRequestChannelSchema = {
    type: requestType,
    phone_number: phoneNumber.trim() || null,
    provider_contact_unit_handle_id: null,
    provider_directory_contact_point_id: contactPointId ?? null,
  };

  if (requestType === 'fax') {
    payload.fax = data.value;
  } else if (requestType === 'email') {
    payload.email = data.value;
  } else if (requestType === 'mail') {
    const parts = [data.address_line1];
    if (data.address_line2) parts.push(data.address_line2);
    parts.push(`${data.city}, ${data.state} ${data.postal_code}`);
    payload.address = parts.join(', ');
  } else if (requestType === 'portal') {
    payload.instructions = data.value;
  }

  return payload;
};

export const SelectDestinationModal = ({
  isOpen,
  onClose,
  request,
  providerId,
  matterId,
  provider,
  onSuccess,
}: SelectDestinationModalProps) => {
  const providerServiceId = provider?.provider_service_ref_id ?? null;
  const scope = useMemo(() => getScopeFromRequest(request), [request]);
  const providerName = provider?.name ?? 'Provider';
  const providerCity = useMemo(() => {
    const address = provider?.address ?? '';
    return (
      address
        .split(',')
        .map((part) => part.trim())
        .find((_value, index, parts) => index === parts.length - 2) ?? ''
    );
  }, [provider?.address]);

  const [viewMode, setViewMode] = useState<ViewMode>('picker');
  const [phoneNumber, setPhoneNumber] = useState(
    request.channel?.phone_number ?? '',
  );
  const [savedPhoneNumber, setSavedPhoneNumber] = useState(
    request.channel?.phone_number ?? '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const phoneByRequestRef = useRef<Record<string, string>>({});

  const [setDestination] = useProviderServiceDestinationsUpdateMutation();
  const [updateChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();
  const [createChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();

  useEffect(() => {
    if (!isOpen) return;
    setViewMode(providerServiceId ? 'picker' : 'manual');
    const cachedPhone = phoneByRequestRef.current[request.id];
    const initialPhone = cachedPhone ?? request.channel?.phone_number ?? '';
    setPhoneNumber(initialPhone);
    setSavedPhoneNumber(initialPhone);
  }, [isOpen, providerServiceId, request.channel?.phone_number, request.id]);

  const normalizePhone = (value: string): string => value.trim();
  const hasUnsavedPhoneChanges =
    normalizePhone(phoneNumber) !== normalizePhone(savedPhoneNumber);

  const upsertRequestChannel = async (
    payload: BaseTreatmentRecordRequestChannelSchema,
  ) => {
    if (request.channel?.id) {
      await updateChannel({
        matterId,
        providerId,
        requestId: request.id,
        channelId: request.channel.id,
        updateTreatmentRecordRequestChannelSchema: payload,
      }).unwrap();
      return;
    }

    await createChannel({
      matterId,
      providerId,
      requestId: request.id,
      createTreatmentRecordRequestChannelSchema: {
        ...payload,
        type: payload.type,
      },
    }).unwrap();
  };

  const maybeSavePreferredDestination = async (
    contactPointId: string,
    mode: SaveMode,
    evidenceSourceType?: 'USER' | 'PARALLEL' | 'VOICEAI' | 'SYSTEM',
    evidenceNote?: string,
  ) => {
    if (!providerServiceId || mode !== SaveMode.SAVE_FOR_FUTURE) return;

    try {
      await setDestination({
        providerServiceId,
        patchedProviderServiceDestinationsPatchRequest: {
          scope,
          contact_point_id: contactPointId,
          save_mode: mode,
          evidence: evidenceSourceType
            ? {
                source_type: evidenceSourceType,
                note: evidenceNote || undefined,
              }
            : undefined,
        },
      }).unwrap();
    } catch {
      toast.error('Request updated, but failed to save preferred destination');
    }
  };

  const handleChooseDestination = async (destination: DestinationOption) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await maybeSavePreferredDestination(
        destination.contact_point_id,
        SaveMode.SAVE_FOR_FUTURE,
        'SYSTEM',
        typeof destination.evidence_summary === 'string'
          ? destination.evidence_summary
          : undefined,
      );

      await upsertRequestChannel(
        buildChannelPayloadFromDestination(destination, phoneNumber),
      );
      const normalizedPhone = normalizePhone(phoneNumber);
      phoneByRequestRef.current[request.id] = normalizedPhone;
      setSavedPhoneNumber(normalizedPhone);

      toast.success('Destination updated');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Failed to update destination');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSubmit = async (data: ManualDestinationFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let contactPointId: string | null = null;

      if (providerServiceId) {
        const response = await setDestination({
          providerServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope,
            save_mode: data.save_mode,
            manual_destination:
              data.channel_type === DestinationChannelType.MAIL
                ? {
                    channel_type: data.channel_type,
                    address_line1: data.address_line1,
                    address_line2: data.address_line2 || undefined,
                    city: data.city,
                    state: data.state,
                    postal_code: data.postal_code,
                  }
                : {
                    channel_type: data.channel_type,
                    value: data.value,
                  },
            evidence: {
              source_type: UI_MANUAL_EVIDENCE_TO_API_SOURCE[data.evidence_type],
              note: data.evidence_detail?.trim() || undefined,
            },
          },
        }).unwrap();

        const scoped = response.scopes.find((entry) => entry.scope === scope);
        contactPointId = scoped?.recommended?.contact_point_id ?? null;
      }

      await upsertRequestChannel(
        buildChannelPayloadFromManual(data, phoneNumber, contactPointId),
      );
      const normalizedPhone = normalizePhone(phoneNumber);
      phoneByRequestRef.current[request.id] = normalizedPhone;
      setSavedPhoneNumber(normalizedPhone);

      toast.success('Destination updated');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Failed to update destination');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavePhoneOnly = async () => {
    if (isSubmitting || !request.channel?.id) return;

    setIsSubmitting(true);
    try {
      const normalizedPhone = normalizePhone(phoneNumber);
      await updateChannel({
        matterId,
        providerId,
        requestId: request.id,
        channelId: request.channel.id,
        updateTreatmentRecordRequestChannelSchema: {
          phone_number: normalizedPhone || null,
        },
      }).unwrap();

      phoneByRequestRef.current[request.id] = normalizedPhone;
      setSavedPhoneNumber(normalizedPhone);
      toast.success('Contact phone saved');
      onSuccess?.();
    } catch {
      toast.error('Failed to save contact phone');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPhoneField = () => (
    <Field>
      <Label>Contact phone (optional)</Label>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        placeholder="Enter phone number"
      />
    </Field>
  );

  const renderPhoneSaveControls = () => (
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-zinc-500">
        {request.channel?.id
          ? 'Save phone to update this request channel.'
          : 'Select a destination first to save a contact phone for this request.'}
      </p>
      <Button
        type="button"
        outline
        onClick={handleSavePhoneOnly}
        disabled={!request.channel?.id || !hasUnsavedPhoneChanges || isSubmitting}
      >
        Save phone
      </Button>
    </div>
  );

  const renderBody = () => {
    if (!providerServiceId) {
      return (
        <div className="space-y-4">
          {renderPhoneField()}
          {renderPhoneSaveControls()}
          <ManualDestinationForm
            onSubmit={handleManualSubmit}
            onCancel={onClose}
          />
        </div>
      );
    }

    if (viewMode === 'search') {
      return (
        <div className="space-y-4">
          <DestinationSearchPanel
            mode="inline"
            open
            onOpenChange={() => undefined}
            onBack={() => setViewMode('picker')}
            providerServiceId={providerServiceId}
            scope={scope}
            providerName={providerName}
            providerCity={providerCity}
            onSelectResult={handleChooseDestination}
          />

          <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-4">
            {renderPhoneField()}
            {renderPhoneSaveControls()}
          </div>
        </div>
      );
    }

    if (viewMode === 'manual') {
      return (
        <div className="space-y-4">
          <Button
            type="button"
            outline
            onClick={() => setViewMode('picker')}
            className="gap-1"
          >
            <ArrowLeft className="size-3.5" />
            Back
          </Button>
          {renderPhoneField()}
          {renderPhoneSaveControls()}
          <ManualDestinationForm
            onSubmit={handleManualSubmit}
            onCancel={() => setViewMode('picker')}
            defaultChannelType={DestinationChannelType.FAX}
          />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {isSubmitting && (
          <div className="flex items-center gap-2 rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
            <Loader2 className="size-4 animate-spin" />
            Saving changes...
          </div>
        )}

        <DestinationPicker
          scope={scope}
          providerServiceId={providerServiceId}
          onSelectDestination={handleChooseDestination}
          onOpenSearch={() => setViewMode('search')}
          onOpenManualEntry={() => setViewMode('manual')}
        />

        <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-4">
          {renderPhoneField()}
          {renderPhoneSaveControls()}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="3xl"
      zIndex={Z_INDEX_MODAL_NESTED}
      nested
    >
      <DialogTitle>Select Destination</DialogTitle>
      <DialogDescription>
        Choose the destination for this request.
      </DialogDescription>
      {/* Portal info banner when current channel is portal */}
      {request.channel?.type === 'portal' && request.channel?.portal_provider && (
        <div className="mx-6 mt-2 rounded-md border border-primary/20 bg-primary-light px-3 py-2 text-sm font-medium text-primary">
          <span className="font-semibold">
            {request.channel.portal_provider.portal === 'chartswap' ? 'ChartSwap' : request.channel.portal_provider.portal === 'smart_request' ? 'SmartRequest' : request.channel.portal_provider.portal === 'mro' ? 'MRO' : request.channel.portal_provider.portal}
          </span>
          {' \u00b7 '}
          {request.channel.portal_provider.name}
          {' \u2014 Login credentials are entered at submission time.'}
        </div>
      )}
      <DialogBody>{renderBody()}</DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={isSubmitting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
